import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/transactions")({
  component: TransactionsPage,
});

import { useState } from "react";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { useFetchTransactions } from "@/hooks/api/inventory/useStockOps";
import {
  useFetchWarehouses,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { TransactionTable } from "@/components/inventory/tables/TransactionTable";
import { ItemSearchFilter } from "@/components/inventory/shared/ItemSearchFilter";

const TX_TYPE_OPTIONS = [
  { value: "IN", label: "Stock In" },
  { value: "OUT", label: "Stock Out" },
  { value: "TRANSFER_IN", label: "Transfer In" },
  { value: "TRANSFER_OUT", label: "Transfer Out" },
  { value: "ADJUSTMENT", label: "Adjustment" },
  { value: "RETURN", label: "Return" },
  { value: "DAMAGE", label: "Damage" },
];

function TransactionsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [txTypeFilter, setTxTypeFilter] = useState<string | undefined>(
    undefined
  );
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [itemFilter, setItemFilter] = useState<string | undefined>(undefined);

  const transactionsQuery = useFetchTransactions({
    page_size: pageSize,
    page,
    ordering: "-created_at",
    transaction_type: txTypeFilter || undefined,
    warehouse: warehouseFilter ? Number(warehouseFilter) : undefined,
    item: itemFilter ? Number(itemFilter) : undefined,
  });
  const transactions = transactionsQuery.data?.results ?? [];
  const count = transactionsQuery.data?.count ?? 0;
  const loading = transactionsQuery.isPending;
  const isFetching = transactionsQuery.isFetching;

  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];

  const resetFilters = () => {
    setTxTypeFilter(undefined);
    setWarehouseFilter(undefined);
    setItemFilter(undefined);
    setPage(1);
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Stock Transactions"
          description="Immutable ledger of all inventory movements"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SelectFilter
            placeholder="Transaction Type"
            options={TX_TYPE_OPTIONS}
            value={txTypeFilter}
            onChange={(v) => {
              setTxTypeFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={warehouseFilter}
            onChange={(v) => {
              setWarehouseFilter(v);
              setPage(1);
            }}
          />
          <ItemSearchFilter
            value={itemFilter}
            onChange={(id) => {
              setItemFilter(id == null ? undefined : String(id));
              setPage(1);
            }}
          />
        </FiltersPanel>

        <TransactionTable transactions={transactions} isLoading={loading} />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={(p) => setPage(p)}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>
    </SidebarInset>
  );
}
